import Component from '@glimmer/component';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { IntlService } from 'ember-intl';
import AlertBanner from 'tio-ui/components/alert-banner';
import Button from 'tio-ui/components/button';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type LocalePreferenceService from 'tio-employee/services/locale-preference';
import type { SupportedLocale } from 'tio-employee/services/locale-preference';
import type PartnerService from 'tio-employee/services/partner';

/** Asks a UPS employee whose browser lists French to confirm French; either answer saves a locale. */
export default class DashboardLocaleAlert extends Component {
  @service declare intl: IntlService;
  @service declare localePreference: LocalePreferenceService;
  @service declare notifications: NotificationsService;
  @service declare partner: PartnerService;

  get isShown() {
    return (
      this.partner.isUps &&
      this.localePreference.savesToAccount &&
      !this.localePreference.saved &&
      this.localePreference.browserListsFrench
    );
  }

  confirm = dropTask(async () => {
    await this.#answer('fr-ca');
  });

  decline = dropTask(async () => {
    await this.#answer('en-us');
  });

  get isAnswering() {
    return this.confirm.isRunning || this.decline.isRunning;
  }

  async #answer(locale: SupportedLocale) {
    let remembered: boolean;
    try {
      remembered = await this.localePreference.save(locale);
    } catch (error) {
      console.error(error);
      this.notifications.add(this.intl.t('profile.language.save_error'), { appearance: 'error' });
      return;
    }
    if (remembered) this.localePreference.reloadUnlessShowing(locale);
  }

  <template>
    {{#if this.isShown}}
      <AlertBanner
        @intent="info"
        @title={{t "dashboard_locale_alert.title"}}
        @live={{false}}
        aria-label={{t "dashboard_locale_alert.title"}}
        data-test-dashboard-locale-alert
      >
        {{! Buttons sit under the text: the action slot keeps its width and crushes the text at phone width. }}
        <div class="tio-col">
          <span>{{t "dashboard_locale_alert.body"}}</span>
          <div class="tio-cluster">
            <Button
              @intent="primary"
              @size="sm"
              @isRunning={{this.confirm.isRunning}}
              @isDisabled={{this.isAnswering}}
              {{on "click" this.confirm.perform}}
              data-test-dashboard-locale-alert-confirm
            >
              {{t "dashboard_locale_alert.confirm"}}
            </Button>
            <Button
              @appearance="outlined"
              @size="sm"
              @isRunning={{this.decline.isRunning}}
              @isDisabled={{this.isAnswering}}
              {{on "click" this.decline.perform}}
              data-test-dashboard-locale-alert-decline
            >
              {{t "dashboard_locale_alert.decline"}}
            </Button>
          </div>
        </div>
      </AlertBanner>
    {{/if}}
  </template>
}
